import '../../../App.css'
import {Breadcrumb, Col, Row, Card, Statistic, Typography, Layout, Progress, Table, Tag, Timeline, Alert, Space, Spin, Dropdown, Menu, theme} from 'antd';
import {useEffect, useState} from "react";
import {
  InfoCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  BugOutlined
} from '@ant-design/icons';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { useTranslation } from 'react-i18next';
import { schedulerClient } from '../../../api/scheduler-client';

const { Title, Text } = Typography;
const { Content } = Layout;

// Dashboard configuration - can be moved to settings/config later
export interface DashboardConfig {
  errorRateThreshold: number; // Percentage threshold for high error rate alerts (0-100)
  errorRateColorThresholds: {
    good: number; // Green threshold (below this value)
    warning: number; // Orange threshold (between good and warning)
    // Red is anything above warning threshold
  };
  successRateColorThresholds: {
    excellent: number; // Green threshold (above this value)
    good: number; // Orange threshold (between good and excellent)
    // Red is anything below good threshold
  };
  pollingIntervalSeconds: number; // Real-time polling interval in seconds
}

export const DEFAULT_DASHBOARD_CONFIG: DashboardConfig = {
  errorRateThreshold: 5.0, // 5% default threshold for alerts
  errorRateColorThresholds: {
    good: 5.0, // Green below 5%
    warning: 10.0 // Orange between 5-10%, Red above 10%
  },
  successRateColorThresholds: {
    excellent: 95.0, // Green above 95%
    good: 90.0 // Orange between 90-95%, Red below 90%
  },
  pollingIntervalSeconds: 2.0 // Poll every 2 seconds for real-time updates
};

// Type definitions
interface ExecutionData {
  execution_id: number;
  job_id: string;
  job_name: string;
  execution_status: string;
  creation_user: string;
  environment: string;
  creation_date_time: Date;
  run_start_date_time: Date | null;
  finished_date_time: Date | null;
  records_processed: number;
  duration: number | null;
}

// Combined execution data from multiple APIs
interface CombinedExecutionData {
  execution_id: number;
  job_name: string;
  execution_status: string;
  environment: string;
  duration: number | null;
  lines_input: number;
  lines_output: number;
  lines_read: number;
  lines_written: number;
  lines_updated: number;
  lines_rejected: number;
  creation_user: string;
  creation_date_time: Date;
}

interface MetricData {
  hour: number;
  executions: number;
  throughput: number;
  avg_duration: number;
  success_rate: number;
  cpu_usage: number;
  memory_usage: number;
}

interface LogData {
  id: number;
  timestamp: Date;
  level: string;
  message: string;
  execution_id: number;
  channel_id: string;
}

interface SubJobData {
  id: number;
  name: string;
  status: string;
  start_time: Date;
  end_time: Date | null;
  records_processed: number;
  description: string;
}

// Mock data generators
const generateMockExecutions = (): ExecutionData[] => {
  const statuses = ['RUNNING', 'COMPLETED', 'FAILED', 'PENDING', 'CANCELLED'];
  const jobTypes = ['Customer Data ETL', 'Sales Analytics', 'Data Warehouse Sync', 'ML Feature Pipeline', 'Report Generation'];
  const environments = ['production', 'staging', 'development'];
  const users = ['john.doe', 'jane.smith', 'mike.wilson', 'sarah.connor', 'alex.chen'];
  
  return Array.from({ length: 50 }, (_, i) => {
    const startTime = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    const duration = Math.random() * 120 * 60 * 1000; // 0-2 hours
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    return {
      execution_id: 1000 + i,
      job_id: `job_${jobTypes[Math.floor(Math.random() * jobTypes.length)].replace(/\s+/g, '_').toLowerCase()}`,
      job_name: jobTypes[Math.floor(Math.random() * jobTypes.length)],
      execution_status: status,
      creation_user: users[Math.floor(Math.random() * users.length)],
      environment: environments[Math.floor(Math.random() * environments.length)],
      creation_date_time: startTime,
      run_start_date_time: status !== 'PENDING' ? new Date(startTime.getTime() + 5000) : null,
      finished_date_time: status === 'COMPLETED' || status === 'FAILED' ? new Date(startTime.getTime() + duration) : null,
      records_processed: status !== 'PENDING' ? Math.floor(Math.random() * 1000000) : 0,
      duration: status === 'COMPLETED' || status === 'FAILED' ? Math.floor(duration / 1000) : null
    };
  });
};

const generateMockMetrics = (): MetricData[] => {
  return Array.from({ length: 24 }, (_, i) => ({
    hour: new Date(Date.now() - (23 - i) * 60 * 60 * 1000).getHours(),
    executions: Math.floor(Math.random() * 50) + 10,
    throughput: Math.floor(Math.random() * 500000) + 100000,
    avg_duration: Math.floor(Math.random() * 1800) + 300,
    success_rate: Math.floor(Math.random() * 20) + 80,
    cpu_usage: Math.floor(Math.random() * 40) + 30,
    memory_usage: Math.floor(Math.random() * 30) + 50
  }));
};

const generateSubJobs = (executionId: number): SubJobData[] => {
  const subJobTypes = [
    'Data Extraction', 'Data Validation', 'Data Transformation', 'Data Loading',
    'Schema Validation', 'Quality Checks', 'Index Creation', 'Backup Creation',
    'Notification Sending', 'Cleanup Operations', 'Error Handling', 'Logging',
    'Metadata Update', 'Cache Refresh', 'Statistics Update', 'Reporting'
  ];
  
  const statuses = ['COMPLETED', 'RUNNING', 'FAILED', 'PENDING'];
  const descriptions = [
    'Processing customer data from source',
    'Validating data integrity and format',
    'Applying business rules and transformations',
    'Loading data into target warehouse',
    'Checking schema compatibility',
    'Running quality assurance checks',
    'Creating database indexes',
    'Generating backup snapshots'
  ];
  
  const baseTime = new Date(Date.now() - Math.random() * 2 * 60 * 60 * 1000);
  
  return Array.from({ length: 50 }, (_, i) => {
    const startTime = new Date(baseTime.getTime() + i * 2 * 60 * 1000); // 2 minutes apart
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const duration = Math.random() * 10 * 60 * 1000; // 0-10 minutes
    
    return {
      id: i + 1,
      name: `${subJobTypes[Math.floor(Math.random() * subJobTypes.length)]} ${i + 1}`,
      status,
      start_time: startTime,
      end_time: status === 'COMPLETED' || status === 'FAILED' ? new Date(startTime.getTime() + duration) : null,
      records_processed: Math.floor(Math.random() * 10000),
      description: descriptions[Math.floor(Math.random() * descriptions.length)]
    };
  });
};

function Dashboard() {
    const { token } = theme.useToken();
    const { t } = useTranslation();
    const { trackEvent } = useTelemetry('Dashboard');
    const [loading, setLoading] = useState(true);
    const [executions] = useState(generateMockExecutions());
    const [metrics] = useState(generateMockMetrics());
    const [selectedExecution, setSelectedExecution] = useState<ExecutionData | null>(null);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [tablePageSize, setTablePageSize] = useState(5);
    const [actualExecutionsCount, setActualExecutionsCount] = useState<number | null>(null);
    const [actualFailedExecutionsCount, setActualFailedExecutionsCount] = useState<number | null>(null);
    const [actualRunningExecutionsCount, setActualRunningExecutionsCount] = useState<number | null>(null);
    const [actualCompletedExecutionsCount, setActualCompletedExecutionsCount] = useState<number | null>(null);
    const [recentExecutionsData, setRecentExecutionsData] = useState<CombinedExecutionData[]>([]);
    const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig>(DEFAULT_DASHBOARD_CONFIG);

    // TODO: This configuration should be loaded from and saved to SettingsPanel
    // For future integration:
    // 1. Load dashboardConfig from settings store/context on component mount
    // 2. Provide setDashboardConfig to SettingsPanel for user configuration
    // 3. Persist changes to localStorage or backend settings API
    const updateDashboardConfig = (newConfig: Partial<DashboardConfig>) => {
        setDashboardConfig(prev => ({ ...prev, ...newConfig }));
        // TODO: Persist to settings storage
    };

    // Function to get error rate color based on configurable thresholds
    // Returns: Green (< good%), Orange (good% - warning%), Red (> warning%)
    // Default: Green < 5%, Orange 5-10%, Red > 10%
    const getErrorRateColor = (errorRate: number): string => {
        const thresholds = dashboardConfig.errorRateColorThresholds;
        
        if (errorRate < thresholds.good) {
            return '#52c41a'; // Green - good performance
        } else if (errorRate < thresholds.warning) {
            return '#faad14'; // Orange - warning level
        } else {
            return '#ff4d4f'; // Red - critical level
        }
    };

    // Helper function to get success rate color based on thresholds
    const getSuccessRateColor = (successRate: number): string => {
        const thresholds = dashboardConfig.successRateColorThresholds;
        
        if (successRate > thresholds.excellent) {
            return '#52c41a'; // Green - excellent performance
        } else if (successRate > thresholds.good) {
            return '#faad14'; // Orange - warning level
        } else {
            return '#ff4d4f'; // Red - critical level
        }
    };

    // Function to fetch and combine execution data from multiple APIs
    const fetchRecentExecutions = async (): Promise<void> => {
        try {
            // First, get the list of recent executions
            const response = await fetch(`${schedulerClient['baseURL']}/v1/executions`);
            if (!response.ok) {
                throw new Error('Failed to fetch executions list');
            }
            
            const executionsListData = await response.json();
            const recentExecutionIds = executionsListData.content
                .slice(0, 10) // Get top 10 recent executions
                .map((exec: any) => exec.executionId);

            // Fetch detailed data for each execution in parallel
            const combinedData = await Promise.all(
                recentExecutionIds.map(async (executionId: number) => {
                    try {
                        const [executionDetail, logsDetail, metricsData] = await Promise.all([
                            schedulerClient.getExecutionDetail(executionId),
                            schedulerClient.getExecutionLogsDetail(executionId),
                            schedulerClient.getExecutionMetrics(executionId)
                        ]);

                        // Calculate duration
                        let duration: number | null = null;
                        if (executionDetail.creationDateTime && executionDetail.finishedDateTime) {
                            const start = new Date(executionDetail.creationDateTime).getTime();
                            const end = new Date(executionDetail.finishedDateTime).getTime();
                            duration = Math.round((end - start) / 1000); // Duration in seconds
                        }

                        // Calculate metrics totals
                        const metricsTotal = metricsData.content.reduce((totals, metric) => ({
                            linesInput: totals.linesInput + (metric.linesInput || 0),
                            linesOutput: totals.linesOutput + (metric.linesOutput || 0),
                            linesRead: totals.linesRead + (metric.linesRead || 0),
                            linesWritten: totals.linesWritten + (metric.linesWritten || 0),
                            linesUpdated: totals.linesUpdated + (metric.linesUpdated || 0),
                            linesRejected: totals.linesRejected + (metric.linesRejected || 0)
                        }), { linesInput: 0, linesOutput: 0, linesRead: 0, linesWritten: 0, linesUpdated: 0, linesRejected: 0 });

                        // Combine the data according to the mapping requirements
                        const combinedExecution: CombinedExecutionData = {
                            execution_id: executionId,
                            job_name: logsDetail.name || 'Unknown Job',
                            execution_status: logsDetail.status || 'Unknown',
                            environment: 'unknown', // Environment field not available in API
                            duration: duration,
                            lines_input: metricsTotal.linesInput,
                            lines_output: metricsTotal.linesOutput,
                            lines_read: metricsTotal.linesRead,
                            lines_written: metricsTotal.linesWritten,
                            lines_updated: metricsTotal.linesUpdated,
                            lines_rejected: metricsTotal.linesRejected,
                            creation_user: 'unknown', // Not available in current APIs
                            creation_date_time: new Date(executionDetail.creationDateTime)
                        };

                        return combinedExecution;
                    } catch (error) {
                        logger.error(`Failed to fetch details for execution ${executionId}:`, error);
                        return null;
                    }
                })
            );

            // Filter out failed requests and update state
            const validExecutions = combinedData.filter(exec => exec !== null) as CombinedExecutionData[];
            setRecentExecutionsData(validExecutions);
            
        } catch (error) {
            logger.error('Failed to fetch recent executions:', error);
        }
    };



    useEffect(() => {
        trackEvent('dashboard-mounted');
        
        // Comprehensive function to fetch all real-time metrics
        const fetchAllMetrics = async () => {
            try {
                // Fetch all metrics in parallel for better performance
                const [totalCount, failedCount, runningCount, completedCount] = await Promise.all([
                    schedulerClient.getExecutionsCount().catch(err => {
                        logger.error('Failed to fetch total executions count:', err);
                        return null;
                    }),
                    schedulerClient.getFailedExecutionsCount().catch(err => {
                        logger.error('Failed to fetch failed executions count:', err);
                        return null;
                    }),
                    schedulerClient.getRunningExecutionsCount().catch(err => {
                        logger.error('Failed to fetch running executions count:', err);
                        return null;
                    }),
                    schedulerClient.getCompletedExecutionsCount().catch(err => {
                        logger.error('Failed to fetch completed executions count:', err);
                        return null;
                    })
                ]);

                // Update state with fetched values (null means fall back to mock data)
                setActualExecutionsCount(totalCount);
                setActualFailedExecutionsCount(failedCount);
                setActualRunningExecutionsCount(runningCount);
                setActualCompletedExecutionsCount(completedCount);
            } catch (error) {
                logger.error('Error in fetchAllMetrics:', error);
            }
        };

        // Initial fetch
        fetchAllMetrics();
        fetchRecentExecutions();
        
        // Set up polling interval for real-time updates
        const pollingInterval = setInterval(() => {
            fetchAllMetrics();
            fetchRecentExecutions();
        }, dashboardConfig.pollingIntervalSeconds * 1000);

        // Simulate data loading
        setTimeout(() => setLoading(false), 1000);

        // Cleanup interval on component unmount
        return () => {
            clearInterval(pollingInterval);
        };
    }, [trackEvent, dashboardConfig.pollingIntervalSeconds]);

    // Calculate summary statistics
    const totalExecutions = actualExecutionsCount !== null ? actualExecutionsCount : executions.length;
    const runningExecutions = actualRunningExecutionsCount !== null ? actualRunningExecutionsCount : executions.filter(e => e.execution_status === 'RUNNING').length;
    const completedExecutions = actualCompletedExecutionsCount !== null ? actualCompletedExecutionsCount : executions.filter(e => e.execution_status === 'COMPLETED').length;
    const failedExecutions = actualFailedExecutionsCount !== null ? actualFailedExecutionsCount : executions.filter(e => e.execution_status === 'FAILED').length;
    
    // Calculate error rate percentage
    const errorRate = totalExecutions > 0 ? (failedExecutions / totalExecutions) * 100 : 0;
    
    // Success rate: complement of error rate (100% - error rate)
    const successRate = totalExecutions > 0 ? Math.round(100 - errorRate) : 0;
    
    const totalRecordsProcessed = executions.reduce((sum, e) => sum + (e.records_processed || 0), 0);
    const avgDuration = executions.filter(e => e.duration).reduce((sum, e, _, arr) => sum + (e.duration || 0) / arr.length, 0);

    // Alert categorization
    const alerts = {
        critical: [] as { title: string; description: string; type: 'error' }[],
        warnings: [] as { title: string; description: string; type: 'warning' }[],
        info: [] as { title: string; description: string; type: 'info' }[],
        success: [] as { title: string; description: string; type: 'success' }[]
    };

    // Categorize alerts
    if (failedExecutions > 3) {
        alerts.critical.push({
            title: t('dashboard.alerts.multipleFailedExecutions'),
            description: t('dashboard.alerts.multipleFailedExecutionsDesc', { count: failedExecutions }),
            type: 'error'
        });
    }

    if (metrics[metrics.length - 1]?.memory_usage > 85) {
        alerts.warnings.push({
            title: t('dashboard.alerts.highMemoryUsage'),
            description: t('dashboard.alerts.highMemoryUsageDesc'),
            type: 'warning'
        });
    }

    if (runningExecutions > 10) {
        alerts.info.push({
            title: t('dashboard.alerts.highConcurrentLoad'),
            description: t('dashboard.alerts.highConcurrentLoadDesc', { count: runningExecutions }),
            type: 'info'
        });
    }

    if (runningExecutions === 0) {
        alerts.success.push({
            title: t('dashboard.alerts.noActiveExecutions'),
            description: t('dashboard.alerts.noActiveExecutionsDesc'),
            type: 'success'
        });
    }

    // Create dropdown menu items for alerts
    const getAlertMenuItems = () => {
        const menuItems = [];
        
        if (alerts.critical.length > 0) {
            menuItems.push({
                key: 'critical',
                label: (
                    <div>
                        <Text strong style={{ color: token.colorError }}>{t('dashboard.alerts.critical', { count: alerts.critical.length })}</Text>
                        {alerts.critical.map((alert, index) => (
                            <div key={index} style={{ marginTop: '4px', marginLeft: '8px' }}>
                                <Text type="danger">{alert.title}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>{alert.description}</Text>
                            </div>
                        ))}
                    </div>
                ),
            });
        }
        
        if (alerts.warnings.length > 0) {
            menuItems.push({
                key: 'warnings',
                label: (
                    <div>
                        <Text strong style={{ color: token.colorWarning }}>{t('dashboard.alerts.warnings', { count: alerts.warnings.length })}</Text>
                        {alerts.warnings.map((alert, index) => (
                            <div key={index} style={{ marginTop: '4px', marginLeft: '8px' }}>
                                <Text style={{ color: token.colorWarning }}>{alert.title}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>{alert.description}</Text>
                            </div>
                        ))}
                    </div>
                ),
            });
        }
        
        if (alerts.info.length > 0) {
            menuItems.push({
                key: 'info',
                label: (
                    <div>
                        <Text strong style={{ color: token.colorInfo }}>{t('dashboard.alerts.information', { count: alerts.info.length })}</Text>
                        {alerts.info.map((alert, index) => (
                            <div key={index} style={{ marginTop: '4px', marginLeft: '8px' }}>
                                <Text style={{ color: token.colorInfo }}>{alert.title}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>{alert.description}</Text>
                            </div>
                        ))}
                    </div>
                ),
            });
        }
        
        if (alerts.success.length > 0) {
            menuItems.push({
                key: 'success',
                label: (
                    <div>
                        <Text strong style={{ color: token.colorSuccess }}>{t('dashboard.alerts.allClear', { count: alerts.success.length })}</Text>
                        {alerts.success.map((alert, index) => (
                            <div key={index} style={{ marginTop: '4px', marginLeft: '8px' }}>
                                <Text style={{ color: token.colorSuccess }}>{alert.title}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px' }}>{alert.description}</Text>
                            </div>
                        ))}
                    </div>
                ),
            });
        }
        
        if (menuItems.length === 0) {
            menuItems.push({
                key: 'no-alerts',
                label: <Text type="secondary">{t('dashboard.alerts.noAlerts')}</Text>,
            });
        }
        
        return menuItems;
    };

    const totalAlerts = alerts.critical.length + alerts.warnings.length + alerts.info.length + alerts.success.length;

    // Recent executions for table - use real data if available, fallback to mock
    const recentExecutions = recentExecutionsData.length > 0 
        ? recentExecutionsData.sort((a, b) => new Date(b.creation_date_time).getTime() - new Date(a.creation_date_time).getTime())
        : executions.sort((a, b) => new Date(b.creation_date_time).getTime() - new Date(a.creation_date_time).getTime()).slice(0, 10);

    // Set the first execution as selected by default
    useEffect(() => {
        if (!loading && recentExecutions.length > 0 && !selectedExecution) {
            handleExecutionClick(recentExecutions[0]);
        }
    }, [loading, recentExecutions, selectedExecution]);

    // // Calculate responsive page size based on available height
    // useEffect(() => {
    //     const calculatePageSize = () => {
    //         // Get viewport height
    //         const viewportHeight = window.innerHeight;
            
    //         // Estimate available height for the table section
    //         // Account for: header (60px), breadcrumb (40px), title (40px), alerts row (120px), 
    //         // metrics row (120px), performance row (200px), section title (40px), pagination (50px), margins (80px)
    //         const usedHeight = 60 + 40 + 40 + 120 + 120 + 200 + 40 + 50 + 80;
    //         const availableHeight = viewportHeight - usedHeight;
            
    //         // Each table row is approximately 39px (ant design small table row height)
    //         const rowHeight = 39;
            
    //         // Calculate how many rows can fit, with minimum of 5 and maximum of 20
    //         const calculatedPageSize = Math.max(5, Math.min(20, Math.floor(availableHeight / rowHeight)));
            
    //         setTablePageSize(calculatedPageSize);
    //     };

    //     // Calculate initial page size
    //     calculatePageSize();

    //     // Recalculate on window resize
    //     const handleResize = () => {
    //         calculatePageSize();
    //     };

    //     window.addEventListener('resize', handleResize);
    //     return () => window.removeEventListener('resize', handleResize);
    // }, []);

    // Generate subjobs for selected execution
    const subJobs = selectedExecution ? generateSubJobs(selectedExecution.execution_id) : [];

    // Handle execution selection
    const handleExecutionClick = (execution: ExecutionData | CombinedExecutionData) => {
        // Convert CombinedExecutionData to ExecutionData for compatibility
        if ('job_id' in execution) {
            setSelectedExecution(execution as ExecutionData);
        } else {
            // Convert CombinedExecutionData to ExecutionData format
            const convertedExecution: ExecutionData = {
                execution_id: execution.execution_id,
                job_id: `job_${execution.execution_id}`,
                job_name: execution.job_name,
                execution_status: execution.execution_status,
                creation_user: execution.creation_user,
                environment: execution.environment,
                creation_date_time: execution.creation_date_time,
                run_start_date_time: null,
                finished_date_time: null,
                records_processed: execution.lines_input, // Use lines_input as records_processed for compatibility
                duration: execution.duration
            };
            setSelectedExecution(convertedExecution);
        }
    };

    // Table columns
    const executionColumns = [
      {
        title: t('dashboard.table.jobName'),
        dataIndex: 'job_name',
        key: 'job_name',
        render: (text: string, record: ExecutionData | CombinedExecutionData) => (
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>{t('dashboard.table.id')}: {record.execution_id}</Text>
          </div>
        )
      },
      {
        title: 'Created',
        dataIndex: 'creation_date_time',
        key: 'creation_date_time',
        render: (dateTime: Date) => {
          if (!dateTime) return '-';
          return dateTime.toLocaleString('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).replace('T', ' ');
        }
      },
      {
        title: t('dashboard.table.status'),
        dataIndex: 'execution_status',
        key: 'status',
        render: (status: string) => {
          // Determine color based on status content
          let color = token.colorText; // Default text color
          let tagColor = 'default';
          let icon = <InfoCircleOutlined />;
          
          if (status === 'Finished' || status === 'FINISHED') {
            color = token.colorSuccess; // Green
            tagColor = 'success';
            icon = <CheckCircleOutlined />;
          } else if (status.includes('STOPPED') || status.includes('FAILED') || status.includes('ERROR')) {
            color = token.colorError; // Red
            tagColor = 'error';
            icon = <CloseCircleOutlined />;
          }
          
          return <Tag color={tagColor} icon={icon} style={{ color }}>{status}</Tag>;
        }
      },
      {
        title: t('dashboard.table.environment'),
        dataIndex: 'environment',
        key: 'environment',
        render: (env: string) => <Tag color={env === 'production' ? 'red' : env === 'staging' ? 'orange' : 'blue'}>{env}</Tag>
      },
      {
        title: t('dashboard.table.duration'),
        dataIndex: 'duration',
        key: 'duration',
        render: (duration: number | null) => duration ? `${Math.floor(duration / 60)}m ${duration % 60}s` : '-'
      },
      {
        title: 'Input',
        dataIndex: 'lines_input',
        key: 'lines_input',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          // Handle both data types
          const inputValue = 'lines_input' in record ? record.lines_input : 0;
          return inputValue ? inputValue.toLocaleString() : '0';
        }
      },
      {
        title: 'Output',
        dataIndex: 'lines_output',
        key: 'lines_output',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          const outputValue = 'lines_output' in record ? record.lines_output : 0;
          return outputValue ? outputValue.toLocaleString() : '0';
        }
      },
      {
        title: 'Read',
        dataIndex: 'lines_read',
        key: 'lines_read',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          const readValue = 'lines_read' in record ? record.lines_read : 0;
          return readValue ? readValue.toLocaleString() : '0';
        }
      },
      {
        title: 'Written',
        dataIndex: 'lines_written',
        key: 'lines_written',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          const writtenValue = 'lines_written' in record ? record.lines_written : 0;
          return writtenValue ? writtenValue.toLocaleString() : '0';
        }
      },
      {
        title: 'Updated',
        dataIndex: 'lines_updated',
        key: 'lines_updated',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          const updatedValue = 'lines_updated' in record ? record.lines_updated : 0;
          return updatedValue ? updatedValue.toLocaleString() : '0';
        }
      },
      {
        title: 'Rejected',
        dataIndex: 'lines_rejected',
        key: 'lines_rejected',
        align: 'right' as const,
        render: (value: number, record: ExecutionData | CombinedExecutionData) => {
          const rejectedValue = 'lines_rejected' in record ? record.lines_rejected : 0;
          return rejectedValue ? rejectedValue.toLocaleString() : '0';
        }
      },
      {
        title: t('dashboard.table.user'),
        dataIndex: 'creation_user',
        key: 'user'
      }
    ];

    

    if (loading) {
      return (
        <Layout style={{ height: '100%' }}>
          <Content style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
          </Content>
        </Layout>
      );
    }

    return (
        <Layout style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Content style={{ 
                padding: '24px', 
                flex: 1, 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ flexShrink: 0 }}>
                    <Breadcrumb items={[{ title: t('dashboard.breadcrumb.monitoring') }, { title: t('dashboard.breadcrumb.dashboard') }]} />
                    
                    <Title level={2} style={{ marginTop: '16px', marginBottom: '24px' }}>
                        {t('dashboard.title')}
                    </Title>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 0 }}>
                    {/* Key Metrics Row */}
                    <Row gutter={[16, 16]} style={{ flexShrink: 0 }}>
                        <Col span={4}>
                            <Card>
                                <Statistic
                                    title={t('dashboard.metrics.totalExecutions')}
                                    value={totalExecutions}
                                    prefix={<DatabaseOutlined />}
                                    valueStyle={{ color: token.colorInfo }}
                                />
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card>
                                <Statistic
                                    title={t('dashboard.metrics.currentlyRunning')}
                                    value={runningExecutions}
                                    prefix={<PlayCircleOutlined />}
                                    valueStyle={{ color: token.colorSuccess }}
                                />
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card>
                                <Statistic
                                    title={t('dashboard.metrics.successRate')}
                                    value={successRate}
                                    suffix="%"
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: getSuccessRateColor(successRate) }}
                                />
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card>
                                <Statistic
                                    title={t('dashboard.metrics.recordsProcessed')}
                                    value={totalRecordsProcessed}
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{ color: token.colorPrimary }}
                                    formatter={(value) => `${((value as number) / 1000000).toFixed(1)}M`}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Dropdown 
                                menu={{ items: getAlertMenuItems() }}
                                trigger={['click']}
                                placement="bottomRight"
                            >
                                <Card style={{ cursor: 'pointer' }}>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                            {t('dashboard.systemAlerts')}
                                        </Text>
                                        <Space size="large">
                                            {alerts.critical.length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <CloseCircleOutlined style={{ color: token.colorError, fontSize: '20px' }} />
                                                    <Text strong style={{ color: token.colorError, fontSize: '24px' }}>
                                                        {alerts.critical.length}
                                                    </Text>
                                                </div>
                                            )}
                                            {alerts.warnings.length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <WarningOutlined style={{ color: token.colorWarning, fontSize: '20px' }} />
                                                    <Text strong style={{ color: token.colorWarning, fontSize: '24px' }}>
                                                        {alerts.warnings.length}
                                                    </Text>
                                                </div>
                                            )}
                                            {alerts.info.length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <InfoCircleOutlined style={{ color: token.colorInfo, fontSize: '20px' }} />
                                                    <Text strong style={{ color: token.colorInfo, fontSize: '24px' }}>
                                                        {alerts.info.length}
                                                    </Text>
                                                </div>
                                            )}
                                            {alerts.success.length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: '20px' }} />
                                                    <Text strong style={{ color: token.colorSuccess, fontSize: '24px' }}>
                                                        {alerts.success.length}
                                                    </Text>
                                                </div>
                                            )}
                                            {totalAlerts === 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: '20px' }} />
                                                    <Text strong style={{ color: token.colorSuccess, fontSize: '24px' }}>
                                                        {t('dashboard.allClear')}
                                                    </Text>
                                                </div>
                                            )}
                                        </Space>
                                    </div>
                                </Card>
                            </Dropdown>
                        </Col>
                    </Row>

                    {/* System Performance Row */}
                    <Row gutter={[16, 16]} style={{ flexShrink: 0 }}>
                        <Col span={8}>
                            <Card title={t('dashboard.systemResources')} extra={<CloudServerOutlined />}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text>{t('dashboard.cpuUsage')}</Text>
                                        <Progress 
                                            percent={metrics[metrics.length - 1]?.cpu_usage || 0} 
                                            status={metrics[metrics.length - 1]?.cpu_usage > 80 ? 'exception' : 'active'}
                                            strokeColor={metrics[metrics.length - 1]?.cpu_usage > 80 ? token.colorError : token.colorSuccess}
                                        />
                                    </div>
                                    <div>
                                        <Text>{t('dashboard.memoryUsage')}</Text>
                                        <Progress 
                                            percent={metrics[metrics.length - 1]?.memory_usage || 0} 
                                            status={metrics[metrics.length - 1]?.memory_usage > 85 ? 'exception' : 'active'}
                                            strokeColor={metrics[metrics.length - 1]?.memory_usage > 85 ? token.colorError : token.colorInfo}
                                        />
                                    </div>
                                    <div>
                                        <Text>{t('dashboard.averageDuration')}: </Text>
                                        <Text strong>{Math.floor(avgDuration / 60)}m {Math.floor(avgDuration % 60)}s</Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={t('dashboard.errorSummary')} extra={<BugOutlined />}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Statistic 
                                        title={t('dashboard.failedExecutions')} 
                                        value={failedExecutions} 
                                        valueStyle={{ color: token.colorError }}
                                    />
                                    <Statistic 
                                        title={t('dashboard.errorRate')} 
                                        value={Math.round(errorRate)} 
                                        suffix="%" 
                                        valueStyle={{ color: getErrorRateColor(errorRate) }}
                                    />
                                    {errorRate > dashboardConfig.errorRateThreshold && (
                                        <Alert 
                                            message={t('dashboard.highErrorRateDetected')} 
                                            type="warning" 
                                            showIcon 
                                            icon={<WarningOutlined />}
                                            style={{ fontSize: '12px' }}
                                        />
                                    )}
                                </Space>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title={t('dashboard.throughputTrends')} extra={<InfoCircleOutlined />}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Statistic 
                                        title={t('dashboard.avgRecordsPerHour')} 
                                        value={(totalRecordsProcessed / 24).toFixed(0)} 
                                        valueStyle={{ color: token.colorInfo }}
                                    />
                                    <Statistic 
                                        title={t('dashboard.peakHourThroughput')} 
                                        value={Math.max(...metrics.map(m => m.throughput)).toLocaleString()} 
                                        valueStyle={{ color: token.colorSuccess }}
                                    />
                                    <Text type="secondary">{t('dashboard.last24Hours')}</Text>
                                </Space>
                            </Card>
                        </Col>
                    </Row>

                    {/* Recent Activity Row - Flexible */}
                    <Row gutter={[16, 16]} style={{ flex: 1, minHeight: 0, height: 0 }}>
                        <Col span={16} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Title level={4} style={{ marginBottom: '16px', flexShrink: 0 }}>{t('dashboard.recentExecutions')}</Title>
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <Table 
                                    dataSource={recentExecutions} 
                                    columns={executionColumns}
                                    pagination={{ 
                                        pageSize: tablePageSize,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        showTotal: (total, range) => 
                                            t('dashboard.table.showTotal', { start: range[0], end: range[1], total }),
                                        pageSizeOptions: ['3', '5', '10', '15', '20'],
                                        onShowSizeChange: (current, size) => setTablePageSize(size)
                                    }}
                                    size="small"
                                    rowKey="execution_id"
                                    scroll={{ y: 500 }}
                                    onRow={(record) => ({
                                        onClick: () => handleExecutionClick(record),
                                        style: { 
                                            cursor: 'pointer',
                                            backgroundColor: selectedExecution?.execution_id === record.execution_id ? token.colorPrimaryBg : undefined
                                        }
                                    })}
                                />
                            </div>
                        </Col>
                        <Col span={8} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div 
                                style={{ 
                                    flex: 1, 
                                    minHeight: 0,
                                    overflow: 'auto', 
                                    backgroundColor: token.colorBgLayout,
                                    padding: '16px',
                                    borderRadius: '6px',
                                    border: `1px solid ${token.colorBorder}`
                                }}
                            >
                                {selectedExecution ? (
                                    <>
                                        <div style={{ marginBottom: '16px', borderBottom: `1px solid ${token.colorBorder}`, paddingBottom: '8px' }}>
                                            <Text strong style={{ fontSize: '16px' }}>{selectedExecution.job_name}</Text>
                                            <br />
                                            <Text type="secondary">{t('dashboard.executionId')}: {selectedExecution.execution_id}</Text>
                                        </div>
                                        <Timeline
                                            items={subJobs.map(subJob => ({
                                                color: subJob.status === 'COMPLETED' ? 'green' : 
                                                       subJob.status === 'FAILED' ? 'red' : 
                                                       subJob.status === 'RUNNING' ? 'blue' : 'gray',
                                                children: (
                                                    <div>
                                                        <Text strong>{subJob.name}</Text>
                                                        <br />
                                                        <Text type="secondary">
                                                            {subJob.status} • {subJob.start_time.toLocaleTimeString()}
                                                        </Text>
                                                        <br />
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {subJob.description} • {subJob.records_processed.toLocaleString()} records
                                                        </Text>
                                                    </div>
                                                )
                                            }))}
                                        />
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <Text type="secondary">
                                            {t('dashboard.clickExecutionToView')}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>


                </div>
            </Content>
        </Layout>
    )
}

export default Dashboard